import { createApiClient } from './client'

export interface Photo {
  id: string
  filename: string
  originalName: string
  mimeType: string
  size: number
  tripId: string
  uploadedBy: string
  uploadedAt: string
  url: string
  width?: number
  height?: number
  caption?: string
  takenAt?: string
  uploaderName?: string
  uploaderEmail?: string
  uploaderProfileImagePath?: string | null
  // Optimization status (only use .webp when 'completed')
  optimizationStatus?: 'pending' | 'processing' | 'completed' | 'failed'
  // Social interaction fields (optional, can be fetched separately)
  likeCount?: number
  commentCount?: number
  hasUserLiked?: boolean
}

export interface PhotoUploadLimit {
  usedBytes: number
  limitBytes: number
  remainingBytes: number
  usedPercent: number
  isPremium?: boolean
}

export interface CreatePhotoRequest {
  photo: File
  caption?: string
  takenAt?: string
}

export interface UpdatePhotoRequest {
  caption?: string
  takenAt?: string
}

export interface PhotoWithTrip extends Photo {
  tripName: string
}

export interface PhotosResponse {
  photos: Photo[]
}

export interface AllPhotosResponse {
  photos: PhotoWithTrip[]
  count: number
}

export interface PhotoResponse {
  photo: Photo
}

export interface PhotoUrlResponse {
  url: string
}

// Photos API class
export class PhotosApi {
  private apiClient: ReturnType<typeof createApiClient>
  
  private unwrapResponse<T>(response: any): T {
    if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
      return response.data as T
    }
    return response as T
  }

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Get all photos for a trip
  async getPhotosByTrip(tripId: string): Promise<PhotosResponse> {
    try {
      const raw = await this.apiClient.get<any>(`/files/trip/${tripId}`)
      const unwrapped = this.unwrapResponse<{ files: Photo[]; count: number }>(raw)
      const photos = (unwrapped && unwrapped.files) ? unwrapped.files : []
      return { photos }
    } catch (err) {
      throw err
    }
  }

  // Get photo upload limit for a trip
  async getPhotoUploadLimit(tripId: string): Promise<PhotoUploadLimit> {
    try {
      const raw = await this.apiClient.get<any>(`/files/trip/${tripId}/limit`)
      return this.unwrapResponse<PhotoUploadLimit>(raw)
    } catch (err) {
      throw err
    }
  }

  // Upload a new photo
  async createPhoto(tripId: string, photoData: CreatePhotoRequest): Promise<PhotoResponse> {
    const formData = new FormData()
    formData.append('file', photoData.photo)
    
    if (photoData.caption) {
      formData.append('caption', photoData.caption)
    }
    
    if (photoData.takenAt) {
      formData.append('takenAt', photoData.takenAt)
    }

    // Add tripId to the form data for the backend
    formData.append('tripId', tripId)

    try {
      const raw = await this.apiClient.post<any>(`/files/upload`, formData)
      const photo = this.unwrapResponse<Photo>(raw)
      return { photo }
    } catch (err) {
      throw err
    }
  }

  // Update photo metadata
  async updatePhoto(photoId: string, updateData: UpdatePhotoRequest): Promise<PhotoResponse> {
    try {
      const raw = await this.apiClient.put<any>(`/files/${photoId}`, updateData)
      const photo = this.unwrapResponse<Photo>(raw)
      return { photo }
    } catch (err) {
      throw err
    }
  }

  // Delete a photo
  async deletePhoto(photoId: string): Promise<void> {
    try {
      await this.apiClient.delete(`/files/${photoId}`)
    } catch (err) {
      throw err
    }
  }

  // Get all photos across all trips the user is a member of
  async getAllUserPhotos(): Promise<AllPhotosResponse> {
    try {
      const raw = await this.apiClient.get<any>('/files/library')
      const unwrapped = this.unwrapResponse<{ files: PhotoWithTrip[]; count: number }>(raw)
      return {
        photos: unwrapped?.files ?? [],
        count: unwrapped?.count ?? 0
      }
    } catch (err) {
      throw err
    }
  }

  // Get photo URL
  async getPhotoUrl(photoId: string): Promise<PhotoUrlResponse> {
    try {
      const raw = await this.apiClient.get<any>(`/files/${photoId}/url`)
      const unwrapped = this.unwrapResponse<{ url: string }>(raw)
      return { url: unwrapped.url }
    } catch (err) {
      throw err
    }
  }
}
